import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchActiveSpace } from '../api/space'
import type { Session } from '../api/session'
import { useLoad } from '../api/useLoad'
import { Button } from '../ui/Control'
import { Icon } from '../ui/Icon'
import { Screen } from '../ui/Screen'
import { Spotlight } from '../views/search/Spotlight'
import { Account } from './Account'
import { Connection } from './Connection'
import { Version } from './Version'
import { AREA_IDS, ENTRY_AREA_ID, LISTED_AREAS, areaById } from './areas'
import { useWhatIsOpen } from './open'
import { hrefFor, isPlainClick, navigate, useRoute } from './route'

/**
 * The frame every area runs in.
 *
 * The same four things are in the same four places, whichever area is open: where you are, the way
 * back, the areas themselves, and who you are in which space. An area supplies its content and
 * nothing else — it does not draw its own navigation, decide its own idea of "back", or choose when
 * the layout rearranges. That is the whole point of it being here.
 */
export interface FrameProps {
  readonly session: Session
  /** Called once the session has ended, so the page can go back to the way in. */
  readonly onSignedOut: () => void
}

export function Frame({ session, onSignedOut }: FrameProps) {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const area = areaById(route.areaId)
  // What the area says is open inside it, where it says anything. The bar names the thing in the
  // room rather than the room, once there is more than one thing it could be.
  const open = useWhatIsOpen()
  const where = open ?? area.label
  const main = useRef<HTMLElement>(null)
  /** The area that is open, so a bar that slides sideways can bring it into view. */
  const here = useRef<HTMLAnchorElement>(null)
  const lastArea = useRef(route.areaId)
  /** Whether the search is open over everything. It belongs to the frame, because it is over every area. */
  const [searching, setSearching] = useState(false)
  /**
   * What had the focus when the search was opened, so that closing it hands the focus straight back.
   *
   * Remembered here rather than inside the layer, because by the time the layer exists its own field has
   * already taken the focus — the frame is the only thing that saw what was there before.
   */
  const cameFrom = useRef<Element | null>(null)

  const loadSpace = useCallback((signal: AbortSignal) => fetchActiveSpace(signal), [])
  const { load, retry } = useLoad(loadSpace)

  /*
   * Keeps the address saying where you actually are. An empty address, or one naming an area that
   * does not exist, is corrected to the area on screen — replacing the entry rather than adding
   * one, so the browser's back button does not walk back into an address that was never valid.
   */
  useEffect(() => {
    navigate(route.areaId, route.path, { replace: true })
  }, [route])

  /* A tab among twenty says which part of MyLib it is. */
  useEffect(() => {
    document.title = `${where} — MyLib`
  }, [where])

  /*
   * Where you are has to be visible on a bar that does not show every area at once. Sideways only:
   * moving the page as well would take the content out from under whoever just arrived.
   */
  useEffect(() => {
    here.current?.scrollIntoView?.({ inline: 'center', block: 'nearest' })
  }, [route.areaId])

  /*
   * Changing area moves the focus into the new area, so a keyboard carries on where the eye does
   * rather than starting again at the top of the page.
   *
   * On the area actually having changed, not on the effect running: arriving is not a move, and an
   * effect that re-runs for its own reasons must not take the focus off what somebody was using.
   */
  useEffect(() => {
    if (lastArea.current === route.areaId) {
      return
    }
    lastArea.current = route.areaId
    main.current?.focus()
  }, [route.areaId])

  const goBack = useCallback(() => {
    navigate(route.areaId, route.path.slice(0, -1))
  }, [route])

  /*
   * The two shortcuts into the search, and the one rule they follow: they are shortcuts.
   *
   * ⌘K — Ctrl-K where there is no ⌘ — from anywhere, because that is the key everybody's fingers already
   * know. `/` as well, but only while nothing is being typed into: `/` is a character, and stealing it
   * from somebody naming a folder would be worse than not offering it at all.
   */
  const openSearch = useCallback(() => {
    cameFrom.current = document.activeElement
    setSearching(true)
  }, [])

  const closeSearch = useCallback(() => {
    setSearching(false)
    // Back to the control it was opened from, before React takes the layer away. Anything else would
    // drop somebody at the top of a page they never left.
    if (cameFrom.current instanceof HTMLElement) {
      cameFrom.current.focus()
    }
  }, [])

  useEffect(() => {
    const listen = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        openSearch()
        return
      }
      if (event.key === '/' && !isTyping(event.target)) {
        event.preventDefault()
        openSearch()
      }
    }
    document.addEventListener('keydown', listen)
    return () => {
      document.removeEventListener('keydown', listen)
    }
  }, [openSearch])

  // A refusal is not a fault: the session simply ran out, and the only useful answer is the way
  // back in.
  if (load.status === 'failed' && load.failure.kind === 'refused') {
    return (
      <Screen>
        <p className="card__lead">Your session has ended.</p>
        <p className="card__detail">Signing in again picks up exactly where you were.</p>
        <div className="controls">
          <Button tone="primary" onClick={onSignedOut}>
            Back to sign-in
          </Button>
        </div>
      </Screen>
    )
  }

  const inside = route.path.length > 0

  return (
    <div className="frame" data-shape={area.shape}>
      <Button
        tone="floating"
        className="skip"
        onClick={() => {
          main.current?.focus()
        }}
      >
        Skip to {area.label}
      </Button>

      <header className="bar">
        {inside && (
          <Button
            tone="bare"
            className="bar__back"
            icon="back"
            iconOnlyLabel={`Back to ${area.label}`}
            onClick={goBack}
          />
        )}
        <div className="bar__where">
          <h1 className="bar__title">{where}</h1>
        </div>
        {/*
          The visible way to the search, in the same place in every area. The shortcuts below are
          shortcuts: nothing in MyLib is reachable only by knowing a key combination.
        */}
        <Button
          tone="bare"
          className="bar__search"
          icon="search"
          iconOnlyLabel="Search (⌘K)"
          onClick={openSearch}
        />
        <Account session={session} space={load} onRetrySpace={retry} onSignedOut={onSignedOut} />
      </header>

      <Connection />

      <nav className="nav" aria-label="Areas of MyLib">
        <p className="nav__brand">MyLib</p>
        <ul className="nav__list">
          {LISTED_AREAS.map((candidate) => {
            const current = candidate.id === area.id
            return (
              <li key={candidate.id}>
                <a
                  ref={current ? here : undefined}
                  className="control control--bare nav__link"
                  href={hrefFor(candidate.id)}
                  aria-current={current ? 'page' : undefined}
                  onClick={(event) => {
                    if (!isPlainClick(event)) {
                      return
                    }
                    event.preventDefault()
                    navigate(candidate.id)
                  }}
                >
                  <Icon name={candidate.icon} />
                  <span>{candidate.label}</span>
                </a>
              </li>
            )
          })}
        </ul>
        <Version />
      </nav>

      <main className="main" ref={main} tabIndex={-1} aria-label={area.label}>
        <area.View />
      </main>

      {/*
        Mounted only while it is open, which is what makes Escape able to hand the focus back to whatever
        had it: the layer remembers on mount and restores on unmount.
      */}
      {searching && <Spotlight onClose={closeSearch} />}
    </div>
  )
}

/** Whether this key press belongs to something somebody is typing into. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  )
}
